import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import {
    Bike, Key, TrendingUp, Package, Users, RefreshCcw, Save,
    CheckCircle2, XCircle, Clock, Truck
} from 'lucide-react';

const fmt = (n) => (Number(n) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATUS_LABEL = {
    pending: 'Pendiente',
    assigned: 'Asignado',
    picked_up: 'Recogido',
    in_transit: 'En tránsito',
    delivered: 'Entregado',
    cancelled: 'Cancelado',
    failed: 'Fallido',
};

const STATUS_CLASSES = {
    pending: 'bg-amber-100 text-amber-800',
    assigned: 'bg-blue-100 text-blue-800',
    picked_up: 'bg-indigo-100 text-indigo-800',
    in_transit: 'bg-violet-100 text-violet-800',
    delivered: 'bg-emerald-100 text-emerald-800',
    cancelled: 'border-destructive text-destructive border',
    failed: 'border-red-400 text-red-700 border',
};

export default function AdminOkDelivery() {
    const [tab, setTab] = useState('settings');
    const [settings, setSettings] = useState(null);
    const [baseUrl, setBaseUrl] = useState('');
    const [apiKeyInput, setApiKeyInput] = useState('');
    const [webhookSecretInput, setWebhookSecretInput] = useState('');
    const [stats, setStats] = useState(null);
    const [deliveries, setDeliveries] = useState([]);
    const [driversAll, setDriversAll] = useState([]);
    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const [{ data: s }, { data: st }, { data: d }, { data: dr }] = await Promise.all([
                api.get('/admin/okdelivery/settings'),
                api.get('/admin/okdelivery/stats'),
                api.get('/admin/okdelivery/deliveries?limit=50'),
                api.get('/admin/okdelivery/drivers?limit=50'),
            ]);
            setSettings(s);
            setBaseUrl(s.base_url || '');
            setStats(st);
            setDeliveries(d.items || []);
            setDriversAll(dr.items || []);
        } catch { toast.error('Error al cargar OkDelivery'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const saveSettings = async () => {
        setBusy(true);
        try {
            const payload = { base_url: baseUrl };
            if (apiKeyInput) payload.api_key = apiKeyInput;
            if (webhookSecretInput) payload.webhook_secret = webhookSecretInput;
            await api.put('/admin/okdelivery/settings', payload);
            toast.success('Configuración guardada');
            setApiKeyInput('');
            setWebhookSecretInput('');
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
        finally { setBusy(false); }
    };

    return (
        <div className="space-y-6" data-testid="admin-okdelivery-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                    <Bike className="h-6 w-6 text-[hsl(var(--primary))]" />
                    <h1 className="text-2xl font-semibold">OkDelivery (Picker Express)</h1>
                    {settings?.has_api_key ? (
                        <Badge className="bg-emerald-100 text-emerald-800">
                            <CheckCircle2 className="h-3 w-3 mr-1" />Configurado · {settings.api_key_source}
                        </Badge>
                    ) : (
                        <Badge variant="outline" className="border-destructive text-destructive">
                            <XCircle className="h-3 w-3 mr-1" />Sin API key
                        </Badge>
                    )}
                </div>
                <Button variant="outline" size="sm" onClick={load} disabled={loading}>
                    <RefreshCcw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />Refrescar
                </Button>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="settings" data-testid="okdelivery-settings-tab">
                        <Key className="h-3.5 w-3.5 mr-1" />Configuración
                    </TabsTrigger>
                    <TabsTrigger value="stats" data-testid="okdelivery-stats-tab">
                        <TrendingUp className="h-3.5 w-3.5 mr-1" />Métricas
                    </TabsTrigger>
                    <TabsTrigger value="deliveries" data-testid="okdelivery-deliveries-tab">
                        <Package className="h-3.5 w-3.5 mr-1" />Envíos ({deliveries.length})
                    </TabsTrigger>
                    <TabsTrigger value="drivers" data-testid="okdelivery-drivers-tab">
                        <Users className="h-3.5 w-3.5 mr-1" />Repartidores ({driversAll.length})
                    </TabsTrigger>
                </TabsList>

                {/* ── Settings ── */}
                <TabsContent value="settings" className="pt-4">
                    <Card><CardContent className="p-5 space-y-4 max-w-xl">
                        <div>
                            <Label>Base URL de Picker Express</Label>
                            <Input
                                value={baseUrl}
                                onChange={(e) => setBaseUrl(e.target.value)}
                                placeholder="https://api.pickerexpress.com"
                                data-testid="okdelivery-base-url"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                                URL base de la API de Picker Express. Consulta tu panel en{' '}
                                <a href="https://app.theneo.io/pickerexpress/picker-api-suite" target="_blank" rel="noreferrer" className="text-primary underline">
                                    Picker Express API docs
                                </a>.
                            </p>
                        </div>
                        <div>
                            <Label>API Key (Bearer)</Label>
                            <Input
                                type="password"
                                value={apiKeyInput}
                                onChange={(e) => setApiKeyInput(e.target.value)}
                                placeholder={
                                    settings?.api_key_masked
                                        ? `Actual: ${settings.api_key_masked} (deja vacío para conservar)`
                                        : 'Pega tu API key de Picker Express'
                                }
                                data-testid="okdelivery-api-key"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                                Fuente actual: <strong>{settings?.api_key_source || 'ninguna'}</strong>. La clave se cifra antes de guardarse.
                            </p>
                        </div>
                        <div>
                            <Label>Webhook Secret</Label>
                            <Input
                                type="password"
                                value={webhookSecretInput}
                                onChange={(e) => setWebhookSecretInput(e.target.value)}
                                placeholder={
                                    settings?.webhook_secret_masked
                                        ? `Actual: ${settings.webhook_secret_masked} (deja vacío para conservar)`
                                        : 'Secreto compartido para verificar webhooks'
                                }
                                data-testid="okdelivery-webhook-secret"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                                Fuente actual: <strong>{settings?.webhook_secret_source || 'ninguna'}</strong>.
                                Picker Express firmará los eventos con este secreto (HMAC-SHA256 · <code>X-Picker-Signature</code>). Se cifra en la base de datos.
                            </p>
                        </div>
                        <div className="flex justify-end">
                            <Button onClick={saveSettings} disabled={busy} data-testid="okdelivery-save-settings">
                                <Save className="h-4 w-4 mr-1" />{busy ? 'Guardando…' : 'Guardar'}
                            </Button>
                        </div>
                    </CardContent></Card>
                </TabsContent>

                {/* ── Stats ── */}
                <TabsContent value="stats" className="pt-4">
                    {!stats ? (
                        <div className="text-sm text-muted-foreground text-center py-10">Cargando métricas…</div>
                    ) : (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {[
                                    { label: 'Total envíos', value: stats.total, icon: Package },
                                    { label: 'En tránsito', value: stats.in_transit, icon: Truck, cls: 'text-violet-700' },
                                    { label: 'Entregados', value: stats.delivered, icon: CheckCircle2, cls: 'text-emerald-700' },
                                    { label: 'Cancelados', value: stats.cancelled, icon: XCircle, cls: 'text-destructive' },
                                    { label: 'Pendientes', value: stats.pending, icon: Clock, cls: 'text-amber-700' },
                                    { label: 'Repartidores', value: stats.total_drivers, icon: Users },
                                    { label: 'Activos hoy', value: stats.active_drivers, icon: Bike, cls: 'text-blue-700' },
                                ].map(({ label, value, icon: Icon, cls }) => (
                                    <Card key={label}><CardContent className="p-4">
                                        <div className="flex items-center gap-1.5 text-xs uppercase text-muted-foreground mb-1">
                                            <Icon className="h-3.5 w-3.5" />{label}
                                        </div>
                                        <div className={`text-3xl font-bold ${cls || ''}`}>{value ?? 0}</div>
                                    </CardContent></Card>
                                ))}
                            </div>

                            {(stats.by_day || []).length > 0 && (
                                <Card><CardContent className="p-4">
                                    <h3 className="text-sm font-semibold mb-3">Entregados por día (últimos 7 días)</h3>
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="text-xs uppercase text-muted-foreground border-b">
                                                <th className="text-left py-2">Fecha</th>
                                                <th className="text-right">Envíos</th>
                                                <th className="text-right">Ingreso</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {stats.by_day.map(r => (
                                                <tr key={r.date} className="border-b last:border-0">
                                                    <td className="py-2">{r.date}</td>
                                                    <td className="text-right">{r.count}</td>
                                                    <td className="text-right font-mono">${fmt(r.revenue)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </CardContent></Card>
                            )}
                        </div>
                    )}
                </TabsContent>

                {/* ── Deliveries ── */}
                <TabsContent value="deliveries" className="pt-4">
                    <div className="border rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-xs uppercase text-muted-foreground">
                                <tr>
                                    <th className="p-2 text-left">Negocio</th>
                                    <th className="text-left">Destinatario</th>
                                    <th className="text-left">Destino</th>
                                    <th className="text-left">Repartidor</th>
                                    <th className="text-right">Fee</th>
                                    <th>Estado</th>
                                    <th className="text-right">Fecha</th>
                                </tr>
                            </thead>
                            <tbody>
                                {deliveries.length === 0 && (
                                    <tr><td colSpan="7" className="p-6 text-center text-muted-foreground">Sin envíos registrados todavía.</td></tr>
                                )}
                                {deliveries.map(d => (
                                    <tr key={d.id} className="border-b last:border-0 hover:bg-slate-50">
                                        <td className="p-2 font-medium truncate max-w-[120px]">{d.negocio_name || '—'}</td>
                                        <td className="truncate max-w-[100px]">{d.recipient_name || '—'}</td>
                                        <td className="text-xs truncate max-w-[150px] text-muted-foreground">{d.dropoff_address || '—'}</td>
                                        <td className="text-xs">{d.driver_name || <span className="text-muted-foreground">Sin asignar</span>}</td>
                                        <td className="text-right font-mono">{d.delivery_fee ? `$${fmt(d.delivery_fee)}` : '—'}</td>
                                        <td className="text-center">
                                            <Badge className={STATUS_CLASSES[d.status] || ''}>
                                                {STATUS_LABEL[d.status] || d.status}
                                            </Badge>
                                        </td>
                                        <td className="text-right pr-2 text-xs text-muted-foreground whitespace-nowrap">
                                            {d.created_at ? new Date(d.created_at).toLocaleDateString('es-MX') : '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>

                {/* ── Drivers ── */}
                <TabsContent value="drivers" className="pt-4">
                    <div className="border rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-xs uppercase text-muted-foreground">
                                <tr>
                                    <th className="p-2 text-left">Nombre</th>
                                    <th className="text-left">Negocio</th>
                                    <th className="text-left">Teléfono</th>
                                    <th className="text-left">Vehículo</th>
                                    <th className="text-left">Placa</th>
                                    <th>Estado</th>
                                    <th className="text-right">Alta</th>
                                </tr>
                            </thead>
                            <tbody>
                                {driversAll.length === 0 && (
                                    <tr><td colSpan="7" className="p-6 text-center text-muted-foreground">Sin repartidores registrados todavía.</td></tr>
                                )}
                                {driversAll.map(dr => (
                                    <tr key={dr.id} className="border-b last:border-0 hover:bg-slate-50">
                                        <td className="p-2 font-medium">{dr.name}</td>
                                        <td className="text-xs text-muted-foreground truncate max-w-[120px]">{dr.negocio_name || '—'}</td>
                                        <td>{dr.phone}</td>
                                        <td className="capitalize">{dr.vehicle || '—'}</td>
                                        <td className="font-mono text-xs">{dr.license_plate || '—'}</td>
                                        <td className="text-center">
                                            {dr.active
                                                ? <Badge className="bg-emerald-100 text-emerald-800">Activo</Badge>
                                                : <Badge variant="outline" className="border-muted-foreground text-muted-foreground">Inactivo</Badge>
                                            }
                                        </td>
                                        <td className="text-right pr-2 text-xs text-muted-foreground whitespace-nowrap">
                                            {dr.created_at ? new Date(dr.created_at).toLocaleDateString('es-MX') : '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
}
